import { Dictionary } from "../store/models/common_types";
import {
  CompsTableRow,
  ColumnInfo,
  CompsTable,
  CompsTableColumnData,
} from "./comps-page.model";
import { formatMCapAndEVCell } from "src/app/utils/common";
import { MONTH_DATE_ABB_YEAR_FORMAT } from "src/app/common-constants";
import dayjs from "dayjs";

export function formatCell(data, currency, valueType: string, unit: string) {
  const sliCompsData = data;
  if (sliCompsData === undefined || sliCompsData === null) {
    return "-";
  }

  if (sliCompsData === "NM") {
    return "NM";
  }

  const valueToUse = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Math.abs(sliCompsData));
  let unitType = "";
  unitType = unit;

  if (!currency || valueType === "yoy" || valueType === "revision") {
    currency = "";
  }
  if (unitType === "%") {
    unitType = valueType === "yoy" || valueType === "revision" ? "bps" : "%";
  } else {
    unitType = valueType === "yoy" || valueType === "revision" ? "%" : "";
  }
  if (sliCompsData < 0) {
    return `(${currency}${valueToUse}${unitType})`;
  }

  return `${currency}${valueToUse}${unitType}`;
}

export function createCompTableColumn(
  colInfo: ColumnInfo,
  key: string,
  lineInfo: Dictionary<any>,
  periodLabel?,
): CompsTableColumnData {
  const compsTableColumnData: CompsTableColumnData = {
    key: key, //c0
    sliId: colInfo.sli_id,
    sliCompsId: colInfo.sli_comps_id,
    name: colInfo.name,
    isValuationRatio: colInfo.is_valuation_ratio,
    colDisplayName: periodLabel || key, //cy1
    isFuzzy: colInfo.fuzzysli === 1 ? true : false,
    magnitude: lineInfo[colInfo.sli_comps_id].magnitude,
    unit: lineInfo[colInfo.sli_comps_id].unit,
    average: {
      value: undefined,
      currency: undefined,
    },
    median: {
      value: undefined,
      currency: undefined,
    },
    total: {
      value: undefined,
      currency: undefined,
    },
    tickerCount: {
      values: 0,
      yoy: 0,
      revision: 0,
    },
  };
  return compsTableColumnData;
}

export function createCompsTableFilter(
  viewBy: Array<string>,
  revision: string,
  source: string,
  currency: string,
  lineHasTotal: boolean,
  period?,
  showTotal: boolean = true,
  showMedian: boolean = false,
  showAverage: boolean = true,
): Dictionary<any> {
  return {
    showValue: viewBy.includes("values"),
    showYoy: viewBy.includes("yoy"),
    showRevision: viewBy.includes("revision"),
    showTotal: showTotal,
    showMedian: showMedian,
    showAverage: showAverage,
    revision: revision,
    source: source,
    currency: currency,
    lineHasTotal: lineHasTotal, //introduce new flag here
    selectedPeriod: period,
  };
}

function createMarketColumns(selectedMarketItemList) {
  const marketColumnData = {};
  selectedMarketItemList.forEach((marketItem) => {
    const marketColumn = {
      key: marketItem.id,
      name: marketItem.label as string,
      colDisplayName: marketItem.label,
      average: {
        values: undefined,
        formattedValue: undefined,
        yoy: undefined,
        formattedYoy: undefined,
        revision: undefined,
        formattedRevision: undefined,
        currencySymbol: undefined,
        rd: undefined,
        currency: undefined,
      },
      median: {
        values: undefined,
        formattedValue: undefined,
        yoy: undefined,
        formattedYoy: undefined,
        revision: undefined,
        formattedRevision: undefined,
        currencySymbol: undefined,
        rd: undefined,
        currency: undefined,
      },
      total: {
        values: undefined,
        formattedValue: undefined,
        yoy: undefined,
        formattedYoy: undefined,
        revision: undefined,
        formattedRevision: undefined,
        currencySymbol: undefined,
        rd: undefined,
        currency: undefined,
      },
    };

    marketColumnData[marketItem.id] = marketColumn;
  });
  return marketColumnData;
}

export function createCompsTable(
  rowInfo: Array<CompsTableRow>,
  colInfo: ColumnInfo,
  lineInfo: Dictionary<any>,
  magMsg: string,
  viewBy: Array<string>,
  revision: string,
  source: string,
  currency: string,
  lineHasTotal: boolean,
  marketItems: Array<any> = [],
  showTotal: boolean = true,
  showMedian: boolean = false,
  showAverage: boolean = true,
): CompsTable {
  const compsTable: CompsTable = {
    rows: [],
    columns: [],
    marketColumns: [],
    columnData: {},
    rowData: {},
    filter: {},
    extras: {},
    title: "",
  };

  compsTable.rows = [...rowInfo];
  compsTable.columns = [...colInfo.period];
  compsTable.marketColumns = marketItems;
  const marketColumnsData = createMarketColumns(marketItems);
  const periodLabel = [...colInfo.periodLabel];
  const columnDataMap: Dictionary<CompsTableColumnData> = {};
  compsTable.columns.forEach((id: string, index) => {
    const compsTableColumn: CompsTableColumnData = createCompTableColumn(
      colInfo,
      id,
      lineInfo,
      periodLabel[index],
    );
    columnDataMap[compsTableColumn.key] = compsTableColumn;
  });
  compsTable.columnData = marketColumnsData
    ? { ...marketColumnsData, ...columnDataMap }
    : columnDataMap;
  compsTable.filter = createCompsTableFilter(
    viewBy,
    revision,
    source,
    currency,
    lineHasTotal,
    undefined,
    showTotal,
    showMedian,
    showAverage,
  );
  compsTable.extras = {
    magnitudeMessage: magMsg,
  };

  compsTable.title = colInfo.name;
  return compsTable;
}

export function createComparisionCompsTable(
  tickers,
  lineItems,
  magMsg: string,
  viewBy: Array<string>,
  revision: string,
  source: string,
  currency: string,
  currentPeriod,
  currentPeriodLabel,
  marketItems: Array<any> = [],
  showTotal: boolean = false,
  showMedian: boolean = false,
  showAverage: boolean = false,
) {
  const compsTable: CompsTable = {
    rows: [],
    columns: [],
    columnData: {},
    rowData: {},
    filter: {},
    extras: {},
    title: "",
  };
  const compsColumnData = {};
  const columns = [];
  for (const item of lineItems) {
    const columndata: CompsTableColumnData = {
      key: item.uniquesliid as number,
      sliId: item.uniquesliid as number,
      sliCompsId: item.uniquesliid as number,
      name: item.formatted_name || (item.name as string),
      isValuationRatio: item.is_valuation_ratio,
      colDisplayName: item.formatted_name || item.name,
      isFuzzy: item.fuzzysli === 1 ? true : false,
      magnitude: item.magnitude || "M",
      unit: item.unit || "Amount",
      totaled: item.totaled,
      average: {
        value: undefined,
        currency: undefined,
      },
      median: {
        value: undefined,
        currency: undefined,
      },
      total: {
        value: undefined,
        currency: undefined,
      },
      tickerCount: {
        values: 0,
        yoy: 0,
        revision: 0,
      },
    };
    compsColumnData[columndata.key] = columndata;
    columns.push(columndata.key);
  }
  compsTable.marketColumns = marketItems;
  const marketColumnsData = createMarketColumns(marketItems);
  const rowInfo = createCompsRow(tickers);
  compsTable.rows = [...rowInfo];
  compsTable.columns = columns;
  compsTable.columnData = marketColumnsData
    ? { ...marketColumnsData, ...compsColumnData }
    : compsColumnData;
  compsTable.filter = createCompsTableFilter(
    viewBy,
    revision,
    source,
    currency,
    false,
    currentPeriod,
    showTotal,
    showMedian,
    showAverage,
  );
  compsTable.extras = {
    magnitudeMessage: magMsg,
  };

  compsTable.title = currentPeriodLabel;
  return compsTable;
}

export function createCompsRow(tickers) {
  let result = [];
  result = tickers.map((tickerInfo) => {
    return {
      full_name: tickerInfo.full_name,
      ticker: tickerInfo.ticker,
      sli_comps_id: tickerInfo.companyid,
    };
  });
  return result;
}

export function createTimeseriesCompsTableRows(
  rows: Array<CompsTableRow>,
  allTickerInfo: Dictionary<any>,
  lineId: string,
  periods: Array<string>,
  unit: string,
  marketItems?,
): Dictionary<any> {
  const rowData: Dictionary<any> = {};
  let tickersWithDataCount = 0;
  rows.forEach((row: CompsTableRow) => {
    const rowObj = allTickerInfo[row.ticker] || {};
    if (rowObj) {
      periods.forEach((p) => {
        //value
        const value = rowObj[lineId]
          ? rowObj[lineId][p]
            ? rowObj[lineId][p].values
            : undefined
          : undefined;
        if (value !== null && value !== undefined) {
          rowObj[lineId][p]["formattedValue"] = formatCell(
            value,
            rowObj[`${lineId}|C`],
            "values",
            unit,
          );
        }

        //yoy
        const yoy = rowObj[lineId]
          ? rowObj[lineId][p]
            ? rowObj[lineId][p].yoy
            : undefined
          : undefined;
        if (yoy !== null && yoy !== undefined) {
          rowObj[lineId][p]["formattedYoy"] = formatCell(
            yoy,
            rowObj[`${lineId}|C`],
            "yoy",
            unit,
          );
        }

        //revision
        const rev = rowObj[lineId]
          ? rowObj[lineId][p]
            ? rowObj[lineId][p].revision
            : undefined
          : undefined;
        if (rev !== null && rev !== undefined) {
          rowObj[lineId][p]["formattedRev"] = formatCell(
            rev,
            rowObj[`${lineId}|C`],
            "revision",
            unit,
          );
        }
      });
      if (marketItems && rowObj.market_data) {
        marketItems.forEach((item) => {
          const currencySymbol = rowObj.market_data[item]
            ? rowObj.market_data[`${item}|C`]
            : "";
          const rd = rowObj.market_data[`${item}|RD`];
          const lastDate =
            rowObj.market_data[item] && rowObj.market_data[item].last_date
              ? dayjs(rowObj.market_data[item].last_date).format(
                  MONTH_DATE_ABB_YEAR_FORMAT,
                )
              : undefined;

          const exchangeFullName = rowObj.market_data[item]
            ? rowObj.market_data[item].exchange_full_name
            : "";
          const values = rowObj.market_data[item]
            ? rowObj.market_data[item].values
            : undefined;
          let formattedValue = null;
          if (values !== null && values !== undefined) {
            formattedValue = formatMCapAndEVCell(
              values,
              currencySymbol,
              "values",
            );
          }

          const yoy = rowObj.market_data[item]
            ? rowObj.market_data[item].yoy
            : undefined;
          let formattedYoy = null;
          if (yoy !== null && yoy !== undefined) {
            formattedYoy = formatMCapAndEVCell(yoy, currencySymbol, "yoy");
          }

          const revision = rowObj.market_data[item]
            ? rowObj.market_data[item].revision
            : undefined;
          let formattedRevision = null;
          if (revision !== null && revision !== undefined) {
            formattedRevision = formatMCapAndEVCell(
              revision,
              currencySymbol,
              "revision",
            );
          }

          rowObj["market_data"][item] = {
            values,
            formattedValue,
            yoy,
            formattedYoy,
            revision,
            formattedRevision,
            currencySymbol,
            rd: rd,
            currency: currencySymbol,
            lastDate: lastDate,
            exchangeFullName: exchangeFullName,
          };
        });
      }
    }
    rowData[row.ticker] = rowObj;
    if (rowObj[`${lineId}|data_exists`]) {
      tickersWithDataCount++;
    }
  });

  return { rowData, tickersWithDataCount };
}

export function createComparisionCompsTableRows(
  rows: Array<CompsTableRow>,
  allTickerInfo: Dictionary<any>,
  lineIds: Array<any>,
  period: string,
  marketItems,
): Dictionary<any> {
  const rowData: Dictionary<any> = {};
  const tickersWithDataCount = 0;
  rows.forEach((row: CompsTableRow) => {
    const rowObj = allTickerInfo[row.ticker] || {};
    if (rowObj) {
      lineIds.forEach((item) => {
        //value
        const value = rowObj[item.uniquesliid]
          ? rowObj[item.uniquesliid][period]
            ? rowObj[item.uniquesliid][period].values
            : undefined
          : undefined;
        if (value !== null && value !== undefined) {
          rowObj[item.uniquesliid][period]["formattedValue"] = formatCell(
            value,
            rowObj[`${item.uniquesliid}|C`],
            "values",
            item.unit,
          );
        }

        //yoy
        const yoy = rowObj[item.uniquesliid]
          ? rowObj[item.uniquesliid][period]
            ? rowObj[item.uniquesliid][period].yoy
            : undefined
          : undefined;
        if (yoy !== null && yoy !== undefined) {
          rowObj[item.uniquesliid][period]["formattedYoy"] = formatCell(
            yoy,
            rowObj[`${item.uniquesliid}|C`],
            "yoy",
            item.unit,
          );
        }

        //revision
        const rev = rowObj[item.uniquesliid]
          ? rowObj[item.uniquesliid][period]
            ? rowObj[item.uniquesliid][period].revision
            : undefined
          : undefined;
        if (rev !== null && rev !== undefined) {
          rowObj[item.uniquesliid][period]["formattedRev"] = formatCell(
            rev,
            rowObj[`${item.uniquesliid}|C`],
            "revision",
            item.unit,
          );
        }
      });
      if (marketItems && rowObj.market_data) {
        marketItems.forEach((item) => {
          const currencySymbol = rowObj.market_data[item]
            ? rowObj.market_data[`${item}|C`]
            : "";
          const rd = rowObj.market_data[`${item}|RD`];
          const lastDate =
            rowObj.market_data[item] && rowObj.market_data[item].last_date
              ? dayjs(rowObj.market_data[item].last_date).format(
                  MONTH_DATE_ABB_YEAR_FORMAT,
                )
              : undefined;

          const exchangeFullName = rowObj.market_data[item]
            ? rowObj.market_data[item].exchange_full_name
            : "";
          const values = rowObj.market_data[item]
            ? rowObj.market_data[item].values
            : undefined;
          let formattedValue = null;
          if (values !== null && values !== undefined) {
            formattedValue = formatMCapAndEVCell(
              values,
              currencySymbol,
              "values",
            );
          }

          const yoy = rowObj.market_data[item]
            ? rowObj.market_data[item].yoy
            : undefined;
          let formattedYoy = null;
          if (yoy !== null && yoy !== undefined) {
            formattedYoy = formatMCapAndEVCell(yoy, currencySymbol, "yoy");
          }

          const revision = rowObj.market_data[item]
            ? rowObj.market_data[item].revision
            : undefined;
          let formattedRevision = null;
          if (revision !== null && revision !== undefined) {
            formattedRevision = formatMCapAndEVCell(
              revision,
              currencySymbol,
              "revision",
            );
          }

          rowObj["market_data"][item] = {
            values,
            formattedValue,
            yoy,
            formattedYoy,
            revision,
            formattedRevision,
            currencySymbol,
            rd: rd,
            currency: currencySymbol,
            lastDate: lastDate,
            exchangeFullName: exchangeFullName,
          };
        });
      }
    }
    rowData[row.ticker] = rowObj;
  });

  return { rowData, tickersWithDataCount };
}

/**
 * Calculates yoy value, depending on previous and current year values
 * @param {number} prevValue previous year value
 * @param {number} currentValue current year value
 * @returns {number} avearge yoy value
 */
export function getYoy(prevValue, currentValue) {
  return ((currentValue - prevValue) * 100) / Math.abs(prevValue);
}

/**
 * Calculates average yoy value, it uses two different formula depending on the unit of lineitem
 * @param {string} unitType lineitem unit
 * @param {number} prevValue previous year value
 * @param {number} currentValue current year value
 * @returns {number} avearge yoy value
 */
export function getYoyAverage(unitType, prevValue, currentValue) {
  let avg = null;
  if (unitType === "%") {
    // If the unit is '%', use this formula to calculate
    avg = (currentValue - prevValue) * 100;
  } else {
    // If the unit is non percentage, use yoy formula
    avg = getYoy(prevValue, currentValue);
  }
  return avg;
}

export function computeTotal(
  compsTableData: CompsTable,
  isTimeSeries: boolean,
  selectedPeriod: string,
  currency: string,
  showBlankTickers = false,
  isAbs = false,
) {
  if (!compsTableData || !compsTableData.columns || !compsTableData.filter) {
    return;
  }
  const valueType = compsTableData.filter.showRevision ? "revision" : "values";
  const marketTotalsArray = [];
  // market Columns calculation
  const valueRawFormattedMapping = {
    values: "formattedValue",
    yoy: "formattedYoy",
    revision: "formattedRevision",
  };
  if (compsTableData.marketColumns && compsTableData.marketColumns.length) {
    compsTableData.marketColumns.forEach((item, index) => {
      const id = item.id;
      if (id === 10 || currency === "Base") {
        compsTableData.columnData[id].total = {};
      } else {
        const values: Array<number> = [];
        let currencySymbol = "";
        compsTableData.rows.forEach((row) => {
          if (
            compsTableData.rowData[row.ticker] &&
            compsTableData.rowData[row.ticker].market_data &&
            compsTableData.rowData[row.ticker].market_data[id] &&
            compsTableData.rowData[row.ticker].market_data[id][valueType] !==
              undefined &&
            compsTableData.rowData[row.ticker].market_data[id][valueType] !==
              "NM"
          ) {
            values.push(
              compsTableData.rowData[row.ticker].market_data[id][valueType],
            );
            currencySymbol =
              compsTableData.rowData[row.ticker].market_data[id].currencySymbol;
          }
        });

        if (values.length) {
          const summation = values.reduce((sum, val) => sum + val);
          marketTotalsArray.push(summation);
          if (compsTableData.filter.showYoy) {
            // Calculate only if we hve previous year value
            let yoyTotal = null;
            if (marketTotalsArray[index - 1] !== undefined) {
              yoyTotal = getYoy(
                marketTotalsArray[index - 1],
                marketTotalsArray[index],
              );
            }
            // set the total value of yoy
            compsTableData.columnData[id].total.yoy = yoyTotal;
          }
          compsTableData.columnData[id].total[valueType] = summation;
          compsTableData.columnData[id].total["currencySymbol"] =
            currencySymbol;

          const formattedVal = formatMCapAndEVCell(
            summation,
            currencySymbol || "",
            valueType,
          );
          compsTableData.columnData[id].total[
            valueRawFormattedMapping[valueType]
          ] = formattedVal;
        } else {
          compsTableData.columnData[id].total[valueType] = null;
          compsTableData.columnData[id].total[
            valueRawFormattedMapping[valueType]
          ] = null;
        }
      }
    });
  }

  const totalsArray = [];
  let canLineIdBeTotaledIndex = 0;
  compsTableData.columns.forEach((colKey) => {
    const colSliId = isTimeSeries
      ? compsTableData.columnData[colKey].sliCompsId
      : colKey;
    const period = isTimeSeries ? colKey : selectedPeriod;
    let prevPeriod = "";
    if (compsTableData.filter.showYoy && period !== "LTM") {
      if (period === "NTM") {
        prevPeriod = "LTM";
      } else if (period === "STM") {
        prevPeriod = "NTM";
      } else {
        if (isAbs) {
          const [basis, year] = period.toString().split("-");
          prevPeriod = `${basis}-${(parseInt(year) - 1).toString()}`;
        } else {
          const basis = period.toString().replace(/[0-9-]/g, "");
          const year = period.toString().replace(/[a-zA-Z]/g, "");
          if (basis.includes("Q")) {
            prevPeriod = basis + (parseInt(year) - 4).toString();
          } else if (basis.includes("H")) {
            prevPeriod = basis + (parseInt(year) - 2).toString();
          } else {
            prevPeriod = basis + (parseInt(year) - 1).toString();
          }
        }
      }
    }
    const canLineIdBeTotaled = isTimeSeries
      ? compsTableData.filter.lineHasTotal
      : compsTableData.columnData[colKey].totaled;
    if (
      (currency === "Base" &&
        compsTableData.columnData[colKey].unit === "Amount") ||
      !canLineIdBeTotaled
    ) {
      compsTableData.columnData[colKey].total = {};
    } else {
      const values: Array<number> = [];
      const preValues: Array<number> = [];
      let currency = "";
      compsTableData.rows.forEach((row) => {
        if (
          prevPeriod !== "" &&
          compsTableData.rowData[row.ticker][colSliId] &&
          compsTableData.rowData[row.ticker][colSliId][prevPeriod] &&
          compsTableData.rowData[row.ticker][colSliId][prevPeriod][
            valueType
          ] !== undefined &&
          (!showBlankTickers ||
            (showBlankTickers &&
              compsTableData.rowData[row.ticker][`${colSliId}|data_exists`]))
        ) {
          preValues.push(
            compsTableData.rowData[row.ticker][colSliId][prevPeriod][valueType],
          );
          currency = compsTableData.rowData[row.ticker][`${colSliId}|C`];
        }
        if (
          compsTableData.rowData[row.ticker][colSliId] &&
          compsTableData.rowData[row.ticker][colSliId][period] &&
          compsTableData.rowData[row.ticker][colSliId][period][valueType] !==
            undefined &&
          (!showBlankTickers ||
            (showBlankTickers &&
              compsTableData.rowData[row.ticker][`${colSliId}|data_exists`]))
        ) {
          values.push(
            compsTableData.rowData[row.ticker][colSliId][period][valueType],
          );
          currency = compsTableData.rowData[row.ticker][`${colSliId}|C`];
        }
      });
      if (values.length) {
        const summation = values.reduce((sum, val) => sum + val);
        if (compsTableData.filter.showYoy && period !== "LTM") {
          const prevSummation = preValues.reduce((sum, val) => sum + val);
          totalsArray.push(prevSummation);
        } else {
          totalsArray.push(0);
        }
        totalsArray.push(summation);
        if (compsTableData.filter.showYoy) {
          // Calculate only if we hve previous year value
          let yoyTotal = null;
          // if (totalsArray[index - 1] !== "undefined") {
          if (period !== "LTM") {
            yoyTotal = getYoy(
              totalsArray[2 * canLineIdBeTotaledIndex],
              totalsArray[2 * canLineIdBeTotaledIndex + 1],
            );
          }
          // set the total value of yoy
          compsTableData.columnData[colKey].total.yoy = yoyTotal;
        }
        compsTableData.columnData[colKey].total[valueType] = summation;
        compsTableData.columnData[colKey].total["currency"] = currency;
      } else {
        totalsArray.push(undefined);
        compsTableData.columnData[colKey].total = {};
      }
      canLineIdBeTotaledIndex += 1;
    }
  });
}

export function computeAverage(
  compsTableData: CompsTable,
  isTimeSeries: boolean,
  selectedPeriod: string,
  currency: string,
  showBlankTickers = false,
) {
  if (!compsTableData || !compsTableData.columns || !compsTableData.filter) {
    return;
  }
  const viewBy = [];
  if (compsTableData.filter.showValue) {
    viewBy.push("values");
  }
  if (compsTableData.filter.showYoy) {
    viewBy.push("yoy");
  }
  if (compsTableData.filter.showRevision) {
    viewBy.push("revision");
  }

  // market Columns calculation
  const valueRawFormattedMapping = {
    values: "formattedValue",
    yoy: "formattedYoy",
    revision: "formattedRevision",
  };
  if (compsTableData.marketColumns && compsTableData.marketColumns.length) {
    compsTableData.marketColumns.forEach((item) => {
      const id = item.id;
      viewBy.forEach((valueType) => {
        if (id === 10 || currency === "Base") {
          compsTableData.columnData[id].average = {};
        } else {
          const values: Array<number> = [];
          let currencySymbol = "";
          compsTableData.rows.forEach((row) => {
            if (
              compsTableData.rowData[row.ticker] &&
              compsTableData.rowData[row.ticker].market_data &&
              compsTableData.rowData[row.ticker].market_data[id] &&
              compsTableData.rowData[row.ticker].market_data[id][valueType] !==
                undefined &&
              compsTableData.rowData[row.ticker].market_data[id][valueType] !==
                "NM"
            ) {
              values.push(
                compsTableData.rowData[row.ticker].market_data[id][valueType],
              );
              currencySymbol =
                compsTableData.rowData[row.ticker].market_data[id]
                  .currencySymbol;
            }
          });

          if (values.length) {
            const summation = values.reduce((sum, val) => sum + val);
            const avg = summation / values.length;
            compsTableData.columnData[id].average[valueType] = avg;
            compsTableData.columnData[id].average["currencySymbol"] =
              currencySymbol;

            const formattedVal = formatMCapAndEVCell(
              avg,
              currencySymbol || "",
              valueType,
            );
            compsTableData.columnData[id].average[
              valueRawFormattedMapping[valueType]
            ] = formattedVal;
          } else {
            compsTableData.columnData[id].average[valueType] = null;
            compsTableData.columnData[id].average[
              valueRawFormattedMapping[valueType]
            ] = null;
          }
        }
      });
    });
  }

  compsTableData.columns.forEach((colKey) => {
    const colSliId = isTimeSeries
      ? compsTableData.columnData[colKey].sliCompsId
      : colKey;
    const period = isTimeSeries ? colKey : selectedPeriod;
    viewBy.forEach((valueType) => {
      if (
        currency === "Base" &&
        compsTableData.columnData[colKey].unit === "Amount"
      ) {
        compsTableData.columnData[colKey].average = {};
      } else {
        const values: Array<number> = [];
        let currency = "";
        compsTableData.rows.forEach((row) => {
          if (
            compsTableData.rowData[row.ticker][colSliId] &&
            compsTableData.rowData[row.ticker][colSliId][period] &&
            compsTableData.rowData[row.ticker][colSliId][period][valueType] !==
              undefined &&
            compsTableData.rowData[row.ticker][colSliId][period][valueType] !==
              "NM" &&
            (!showBlankTickers ||
              (showBlankTickers &&
                compsTableData.rowData[row.ticker][`${colSliId}|data_exists`]))
          ) {
            values.push(
              compsTableData.rowData[row.ticker][colSliId][period][valueType],
            );
            currency = compsTableData.rowData[row.ticker][`${colSliId}|C`];
          }
        });
        if (values.length) {
          const summation = values.reduce((sum, val) => sum + val);
          const avg = summation / values.length;
          compsTableData.columnData[colKey].average[valueType] = avg;
          compsTableData.columnData[colKey].average["currency"] = currency;
        } else {
          compsTableData.columnData[colKey].average[valueType] = null;
        }
      }
    });
  });
}

//   export function createCompDetails(
//     uniquesliids: Array<number>,
//     tickers: Array<string>,
//     tabName: string,
//     period: Array<string> | Dictionary<any>,
//     tabType: string,
//     compType: CompTypes,
//     currency: string,
//     source: string,
//     viewBy: Array<string>,
//     revision: string,
//   ): CompDetails {
//     let dataView = {};
//     dataView = { view_by: viewBy };
//     if (revision !== "") {
//       dataView = { view_by: viewBy, revision: revision };
//     }
//     const compDetails: CompDetails = {
//       uniquesliids: uniquesliids,
//       tickers: tickers,
//       tab_name: tabName,
//       period: period,
//       tab_type: tabType,
//       comp_type: compType,
//       currency: currency,
//       source: source,
//       dataView: dataView,
//       revision: revision
//     };

//     return compDetails;
//   }

/**
 * It rounds the number to two decimal point
 * @param {number} num: number
 * @return {number} rounded number
 */
export function roundToTwo(num) {
  return Math.round(num * 100) / 100;
}

/**
 * It return list of selected market id
 * @param {array} marketItemList: market list
 * @return {array} selectedMarketIdList: selected market id
 */
export function getSelectedMarketIdFromList(marketItemList) {
  if (marketItemList && marketItemList.length) {
    return marketItemList.reduce(
      (previousValue, currentValue) =>
        currentValue.checked
          ? previousValue.concat(currentValue.id)
          : previousValue,
      [],
    );
  }
  return [];
}

/**
 * It return list of selected market items
 * @param {array} marketItemList: market list
 * @return {array} selectedMarketList: selected market items
 */
export function getSelectedMarketItemsList(marketItemList) {
  if (marketItemList && marketItemList.length) {
    return marketItemList.filter((item) => item.checked);
  }
  return [];
}

export function computeCompanyCount(
  compsTableData: CompsTable,
  isTimeSeries: boolean,
  selectedPeriod: string,
  showBlankTickers = false,
) {
  if (!compsTableData || !compsTableData.columns || !compsTableData.filter) {
    return;
  }
  const viewBy = [];
  if (compsTableData.filter.showValue) {
    viewBy.push("values");
  }
  if (compsTableData.filter.showYoy) {
    viewBy.push("yoy");
  }
  if (compsTableData.filter.showRevision) {
    viewBy.push("revision");
  }
  // period/line Columns calculation

  compsTableData.columns.forEach((colKey) => {
    let companyCount = 0;
    const colSliId = isTimeSeries
      ? compsTableData.columnData[colKey].sliCompsId
      : colKey;
    const period = isTimeSeries ? colKey : selectedPeriod;
    viewBy.forEach((valueType) => {
      compsTableData.rows.forEach((row) => {
        if (
          compsTableData.rowData[row.ticker] &&
          compsTableData.rowData[row.ticker][colSliId] &&
          compsTableData.rowData[row.ticker][colSliId][period] &&
          compsTableData.rowData[row.ticker][colSliId][period][valueType] !==
            undefined &&
          compsTableData.rowData[row.ticker][colSliId][period][valueType] !==
            "NM" &&
          (showBlankTickers ||
            (!showBlankTickers &&
              compsTableData.rowData[row.ticker][`${colSliId}|data_exists`]))
        ) {
          companyCount += 1;
        }
      });
      compsTableData.columnData[colKey].tickerCount[valueType] =
        companyCount || 0;
    });
  });
}
